import { fetchBillBeta } from '@/api/BillPaymentApi'
import * as Bridge from '@/api/BridgeApi'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import {
  BillPaymentQuickCreditBannerType,
  BILL_PAYMENT_QUICK_CREDIT_BANNER_TYPE,
} from '@/components/version/V3/bill-payment/amount/BillPaymentQuickCreditBanner'
import { EmptySimpleAccount, ISimpleAccountResponse } from '@/domains/AccountDomain'
import { OKSign, STATUS_CODE } from '@/domains/CommonDomain'
import {
  BILLS_IDENTIFIER_TYPE_CODE,
  EmptyBill,
  IBillPaymentInquireRequest,
  IBillPaymentInquireResponse,
  IBillPaymentMaxResponse,
  PAYMENT_TYPE_CODE,
} from '@/domains/version/V2/BillPaymentDomain'
import { IRootState } from '@/modules'
import { simpleAccountSelector } from '@/modules/account/SimpleAccountModule'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import {
  billPaymentMainSelector,
  IFlattenProductType,
} from '@/modules/version/V2/bill-payment/BillPaymentMainModule'
import { createPostAction } from '@/utils/actionUtils'
import { BRIDGE_ERROR_CODE } from '@/utils/bridgeUtils/constants'
import { IAccountInfoResult } from '@/utils/bridgeUtils/features/getMyAccount'
import { IWithdrawalAccountParams } from '@/utils/bridgeUtils/features/getWithdrawalAccount'
import { getRupiahFormatWithoutUnit } from '@/utils/currencyUtils'
import { getFormattedCardNo } from '@/utils/formatUtils'
import { toInteger } from '@/utils/numberUtils'
import { apiSuccessReducer, reset } from '@/utils/reducerUtils'
import { isEmpty, isZero } from '@/utils/validationUtils'
import { Action, createAction, handleActions } from 'redux-actions'
import { call, cancel, put, select, takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'
import { billPaymentProductActions } from './BillPaymentProductModule'

export const BILL_PAYMENT_AMOUNT = 'BillPaymentAmount'

export const VALIDATE_BILL = `${BILL_PAYMENT_AMOUNT}/validateBill`
export const VALIDATE_BILLS = `${BILL_PAYMENT_AMOUNT}/validateBills`
export const VALIDATE_LOAN_AMOUNT_BILL = `${BILL_PAYMENT_AMOUNT}/validateLoanAmtBill`

const SET_CUSTOMER_ID = `${BILL_PAYMENT_AMOUNT}/setCustomerId`
const SET_CUSTOMER_NAME = `${BILL_PAYMENT_AMOUNT}/setCustomerName`
const SET_ACCOUNT_BY_ACCT_NO = `${BILL_PAYMENT_AMOUNT}/selectedAccountByAcctNo`
const SET_ACCOUNT = `${BILL_PAYMENT_AMOUNT}/selectedAccount`
const SET_AMOUNT = `${BILL_PAYMENT_AMOUNT}/amount`
const SET_IS_UNSOLD = `${BILL_PAYMENT_AMOUNT}/isUnSold`
const SET_FEE = `${BILL_PAYMENT_AMOUNT}/fee`
const SET_TICKET_INFO = `${BILL_PAYMENT_AMOUNT}/ticketInfo`
const SET_MAX_AMOUNT = `${BILL_PAYMENT_AMOUNT}/maxAmount`
const SET_WITHDRAWAL_ACCOUNT = `${BILL_PAYMENT_AMOUNT}/setWithdrawalAccount`
const SET_VIEW = `${BILL_PAYMENT_AMOUNT}/setView`
const CLEAR_STATE = `${BILL_PAYMENT_AMOUNT}/clearState`

const SET_RESERVED = `${BILL_PAYMENT_AMOUNT}/setReserve`
const SET_LAST_AMOUNT = `${BILL_PAYMENT_AMOUNT}/lastAmount`
const SET_TOGGLE_BUTTON = `${BILL_PAYMENT_AMOUNT}/toggleButton`
const SET_ACCOUNT_INFO_TOGGLE = `${BILL_PAYMENT_AMOUNT}/toggleAccountInfo`
const SET_LOAN_DISBURSED_FEE = `${BILL_PAYMENT_AMOUNT}/loanDisbursedFee`

export interface IBillPaymentAmountView {
  expandDetailInformation: boolean
  qcBannerType: BillPaymentQuickCreditBannerType
}

export interface IBillPaymentAmountState
  extends IBillPaymentInquireResponse,
    IBillPaymentMaxResponse,
    IBillPaymentReserved,
    IBillPaymentLastAmount,
    IBillPaymentToggleAccount,
    IBillPaymentToggleButton {
  selectedAccount: ISimpleAccountResponse | null
  view: IBillPaymentAmountView
  unSoldTokenYn: OKSign
}

const initialState: IBillPaymentAmountState = {
  ...EmptyBill,
  selectedAccount: null,
  view: {
    expandDetailInformation: false,
    qcBannerType: '',
  },
  maxAmountFlag: false,
  reserved: '',
  ticketInfo1: '',
  ticketInfo2: '',
  ticketInfo3: '',
  ticketInfo4: '',
  merchantName: '',
  lastAmount: '0',
  toggleValue: true,
  toggleAccountValue: true,
  unSoldTokenYn: OKSign.NO,
}

interface IBillPaymentReserved {
  reserved: string
}

interface IBillPaymentLastAmount {
  lastAmount: string
}

interface IBillPaymentToggleButton {
  toggleValue: boolean
}

export interface IBillPaymentToggleAccount {
  toggleAccountValue: boolean
}

export const billPaymentAmountActions = {
  setCustomerId: createAction<string>(SET_CUSTOMER_ID),
  setCustomerName: createAction<string>(SET_CUSTOMER_NAME),
  setAmount: createAction<string>(SET_AMOUNT),
  setIsUnSold: createAction<string>(SET_IS_UNSOLD),
  set: createAction<string>(SET_AMOUNT),
  setFee: createAction<string>(SET_FEE),
  setTicketInfo: createAction<any>(SET_TICKET_INFO),
  setMaxAmount: createAction<boolean>(SET_MAX_AMOUNT),
  setAccountByAcctNo: createAction<string>(SET_ACCOUNT_BY_ACCT_NO),
  setAccount: createAction<Pick<IBillPaymentAmountState, 'selectedAccount'>>(SET_ACCOUNT),
  setWithdrawalAccount: createAction(SET_WITHDRAWAL_ACCOUNT),
  setView: createAction<Partial<IBillPaymentAmountView>>(SET_VIEW),
  clearState: createAction(CLEAR_STATE),
  setReserved: createAction(SET_RESERVED),
  setLastAmount: createAction<string>(SET_LAST_AMOUNT),
  setToggleButton: createAction<boolean>(SET_TOGGLE_BUTTON),
  setAccountInfoToggle: createAction<boolean>(SET_ACCOUNT_INFO_TOGGLE),
  setDisbursedFee: createAction<number>(SET_LOAN_DISBURSED_FEE),
}

export const validateBillAsync = createPostAction<IBillPaymentInquireRequest>(VALIDATE_BILL)
export const validateBillsAsync = createPostAction<IBillPaymentInquireRequest>(VALIDATE_BILLS)
export const validateBillLoanAmtBillAsync = createPostAction<IBillPaymentInquireRequest>(
  VALIDATE_LOAN_AMOUNT_BILL,
)

const reducer = {
  [validateBillAsync.SUCCESS]: apiSuccessReducer,
  [SET_CUSTOMER_ID]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    customerId: action.payload,
  }),
  [SET_CUSTOMER_NAME]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    customerName: action.payload,
  }),
  [SET_AMOUNT]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    amount: action.payload,
  }),
  [SET_IS_UNSOLD]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    unSoldTokenYn: action.payload,
  }),
  [SET_FEE]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    fee: action.payload,
  }),
  [SET_TICKET_INFO]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    ...action.payload,
  }),
  [SET_MAX_AMOUNT]: (state: IBillPaymentAmountState, action: Action<boolean>) => ({
    ...state,
    maxAmountFlag: action.payload,
  }),
  [SET_ACCOUNT]: apiSuccessReducer,
  [SET_VIEW]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
  [CLEAR_STATE]: (state: any) => reset(state, initialState),
  [SET_RESERVED]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    reserved: action.payload,
  }),
  [SET_LAST_AMOUNT]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    lastAmount: action.payload,
  }),
  [SET_TOGGLE_BUTTON]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    toggleValue: action.payload,
  }),
  [SET_ACCOUNT_INFO_TOGGLE]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    toggleAccountValue: action.payload,
  }),

  [SET_LOAN_DISBURSED_FEE]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    disburseFee: action.payload,
  }),
}

export const billPaymentAmountReducer = handleActions(reducer, initialState)

export const billPaymentAmountSelector = {
  view: (state: IRootState): IBillPaymentAmountView =>
    state[BILL_PAYMENT_AMOUNT].view ?? initialState.view,
  selectTicketInfo: (state: IRootState): IBillPaymentAmountState => state[BILL_PAYMENT_AMOUNT],
  totalAmountNum: (state: IRootState): number => {
    const { amount, fee } = state[BILL_PAYMENT_AMOUNT]

    return toInteger(amount || '0') + toInteger(fee || '0')
  },
  isExpandDetailInformation: (state: IRootState, productTypeCode: string): boolean => {
    const { expandDetailInformation }: IBillPaymentAmountView = billPaymentAmountSelector.view(
      state,
    )
    const { paymentTypeCode } = billPaymentMainSelector.flattenProductType(state, productTypeCode)
    const isPulsa = paymentTypeCode === PAYMENT_TYPE_CODE.PULSA

    return isPulsa || (!isPulsa && expandDetailInformation)
  },
  selectedAccount: (state: IRootState) => {
    const majorAccount = simpleAccountSelector.majorAccount(state)

    return oc(state[BILL_PAYMENT_AMOUNT]).selectedAccount(majorAccount) || EmptySimpleAccount
  },
  isTransferLimitOver: (state: IRootState) => {
    const { baseCurJan, creditJan, lonAcctNo } = billPaymentAmountSelector.selectedAccount(state)
    const totalAmountNum = billPaymentAmountSelector.totalAmountNum(state)
    const { qcBannerType } = billPaymentAmountSelector.view(state)

    if (
      lonAcctNo !== '' &&
      qcBannerType !== BILL_PAYMENT_QUICK_CREDIT_BANNER_TYPE.QUICK_CREDIT_ERROR_CANNOT_BE_USED
    ) {
      // 대출 계좌 있음, 대출 가능 상품 및 금액
      if (totalAmountNum < toInteger(baseCurJan)) {
        // 출금 가능(출금금액이 출금계좌 금액 보다 작은경우)
        return false
      } else if (totalAmountNum <= toInteger(baseCurJan) + toInteger(creditJan)) {
        // 출금가능(출금금액이 (출금계좌 금액 + 대출계좌 금액) 보다 작은 경우)
        return false
      } else {
        // 출금 불가
        return true
      }
    } else {
      // 대출 계좌 없음
      return totalAmountNum > toInteger(baseCurJan)
    }
  },
  isAccountLoanOver: (state: IRootState) => {
    const { baseCurJan, creditJan } = billPaymentAmountSelector.selectedAccount(state)
    const { amount } = state[BILL_PAYMENT_AMOUNT]

    return toInteger(amount) > toInteger(baseCurJan) + toInteger(creditJan)
  },
  isValidInput: (state: IRootState, productTypeCode: string): boolean => {
    const {
      productTypeSwitchId,
      productGroupTypeCode,
    } = billPaymentMainSelector.flattenProductType(state, productTypeCode)
    const { acctNo } = billPaymentAmountSelector.selectedAccount(state)
    const { customerId, reserved } = state[BILL_PAYMENT_AMOUNT]
    const isValidAmount = billPaymentAmountSelector.isValidAmount(state)
    const isValidAcctNo = !isEmpty(acctNo) && !isZero(acctNo)
    const isValidCustomerId =
      productGroupTypeCode === '0310' ? true : !isEmpty(customerId) && !isZero(customerId)
    const isValidProductTypeCode = !isEmpty(productTypeCode)
    const isValidProductTypeSwitchId = !isEmpty(productTypeSwitchId)
    const isValidReserved = !isEmpty(reserved)

    // isMaxAmount : productTypeCode 중 금액 직접 입력 상품[오토필 : false, 금액 선택 selectbox 값 없음]만 해당 셀렉터 사용 그 외는 true(!false) 처리
    const isMaxAmount = billPaymentAmountSelector.isMaxAmount(state) ?? false

    return (
      isValidAmount &&
      isValidAcctNo &&
      isValidCustomerId &&
      isValidProductTypeCode &&
      isValidProductTypeSwitchId &&
      isValidReserved &&
      !isMaxAmount
    )
  },
  isValidAmount: (state: IRootState): boolean => {
    const { amount } = state[BILL_PAYMENT_AMOUNT]
    const isTransferLimitOver = billPaymentAmountSelector.isTransferLimitOver(state)

    if (toInteger(amount) <= 0) {
      return false
    }

    return !isTransferLimitOver
  },
  isButtonToggle: (state: IRootState): boolean => {
    const { toggleValue } = state[BILL_PAYMENT_AMOUNT]

    return !toggleValue
  },
  isAccountInfoToggle: (state: IRootState): boolean => {
    const { toggleAccountValue } = state[BILL_PAYMENT_AMOUNT]

    return toggleAccountValue
  },
  isMaxAmount: (state: IRootState): boolean => {
    const { maxAmountFlag } = state[BILL_PAYMENT_AMOUNT]

    return maxAmountFlag
  },
  selectLastAmount: (state: IRootState): string => {
    const { lastAmount } = state[BILL_PAYMENT_AMOUNT]

    return lastAmount
  },

  getCustomerNameInquire: (state: IRootState): string => {
    const { customerName } = state[BILL_PAYMENT_AMOUNT]

    return customerName
  },
}

export function* validateBill(action: Action<IBillPaymentInquireRequest>) {
  try {
    yield put(loadingActions.start(VALIDATE_BILL))
    const response: IBillPaymentInquireResponse = yield call(fetchBillBeta, action.payload)
    yield put(validateBillAsync.success(response))

    const { productTypeIdentifierType }: IFlattenProductType = yield select(
      billPaymentMainSelector.flattenProductType,
      action.payload.productTypeCode,
    )

    const formattedCustomerId =
      productTypeIdentifierType === BILLS_IDENTIFIER_TYPE_CODE.CREDITCARD
        ? getFormattedCardNo(response.customerId)
        : response.customerId

    /**
     * TODO: CREATE LOGIC FOR ADDING A UNSOLD TOKEN FROM INQUIRE RESULT
     * TO THE EXISTING DENOM
     *
     *
     */

    yield put(billPaymentAmountActions.setCustomerId(formattedCustomerId))
  } catch (e) {
    const errorObj: any = e
    if (errorObj.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_FAIL) {
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL))
    } else {
      yield put(errorActions.set(errorObj))
    }
    // 에러인 경우 동일 한 금액으로도 validateBill 재 호출 하도록 값 강제수정
    yield put(billPaymentAmountActions.setLastAmount('0'))
  } finally {
    yield put(loadingActions.finish(VALIDATE_BILL))
  }
}

// bills 상품군 전용
export function* validateBills(action: Action<IBillPaymentInquireRequest>) {
  try {
    yield put(loadingActions.start(VALIDATE_BILL))
    const response: IBillPaymentInquireResponse = yield call(fetchBillBeta, action.payload)
    const customerName = response.customerName

    const ticketInfo1 = response.ticketInfo1 ?? ''
    const ticketInfo2 = response.ticketInfo2 ?? ''
    const ticketInfo3 = response.ticketInfo3 ?? ''
    const ticketInfo4 = response.ticketInfo4 ?? ''
    const merchantName = response.merchantName ?? ''

    yield put(validateBillAsync.success(response))

    yield put(loadingActions.finish(VALIDATE_BILL))

    const { productGroupTypeCode } = yield select(state =>
      billPaymentMainSelector.flattenProductType(state, action.payload.productTypeCode),
    )

    yield put(
      billPaymentProductActions.submit({
        productGroupTypeCode,
        customerName,
        ticketInfo1,
        ticketInfo2,
        ticketInfo3,
        ticketInfo4,
        merchantName,
      }),
    )
  } catch (e) {
    const errorObj: any = e
    yield put(billPaymentProductActions.exceptionError(errorObj))
  } finally {
    yield put(loadingActions.finish(VALIDATE_BILL))
  }
}

export function* validateLoanAmountBill(action: Action<IBillPaymentInquireRequest>) {
  try {
    yield put(loadingActions.start(VALIDATE_BILL))
    const response: IBillPaymentInquireResponse = yield call(fetchBillBeta, action.payload)
    yield put(billPaymentAmountActions.setDisbursedFee(response.disburseFee ?? 0))
  } catch (e) {
    const errorObj: any = e
    yield put(billPaymentProductActions.exceptionError(errorObj))
  } finally {
    yield put(loadingActions.finish(VALIDATE_BILL))
  }
}

export function* setWithdrawalAccount() {
  const { acctNo: selectedAccountNo }: ISimpleAccountResponse = yield select(
    billPaymentAmountSelector.selectedAccount,
  )
  const totalAmountNum = yield select(billPaymentAmountSelector.totalAmountNum)
  const amount = getRupiahFormatWithoutUnit(totalAmountNum)

  const param: IWithdrawalAccountParams = {
    title: 'Withdraw Account',
    selectedAccountNo,
    amount,
  }

  try {
    const { result: selectedAccount }: IAccountInfoResult = yield call(
      Bridge.getWithdrawalAccount,
      param,
    )

    if (selectedAccount) {
      yield put(
        billPaymentAmountActions.setAccount({
          selectedAccount,
        }),
      )
    }
  } catch (e) {
    const errorObj: any = e
    if (errorObj.code === BRIDGE_ERROR_CODE.CLOSE) {
      yield cancel()
    }

    yield put(errorActions.set(errorObj))
  }
}

export function* setAccountByAcctNo(action: Action<string>) {
  const acctNo = action.payload
  const accounts: ISimpleAccountResponse[] = yield select(simpleAccountSelector.simpleAccounts)
  const selectedAccount = accounts.find(account => account.acctNo === acctNo) || EmptySimpleAccount

  yield put(
    billPaymentAmountActions.setAccount({
      selectedAccount,
    }),
  )
}

export const billPaymentAmountSaga = [
  takeLatest(validateBillAsync.POST, validateBill),
  takeLatest(validateBillsAsync.POST, validateBills),
  takeLatest(validateBillLoanAmtBillAsync.POST, validateLoanAmountBill),
  takeLatest(SET_WITHDRAWAL_ACCOUNT, setWithdrawalAccount),
  takeLatest(SET_ACCOUNT_BY_ACCT_NO, setAccountByAcctNo),
]
